import type { Metric } from 'web-vitals';

interface VitalsData {
  ttfb?: number;
  fcp?: number;
  lcp?: number;
  inp?: number;
  cls?: number;
}

interface NavigationData {
  loadDuration?: number;
  domContentLoaded?: number;
}

interface ResourceData {
  jsTransferBytes: number;
  cssTransferBytes: number;
  jsDecodedBytes: number;
  jsCount: number;
  cssCount: number;
}

export default defineNuxtPlugin((nuxtApp) => {
  const app = useNuxtApp();
  if (!import.meta.client) return;

  const vitals: VitalsData = {};
  let hydrationMs: number | undefined;
  let sendCount = 0;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;

  let hydrationStart: number | undefined;

  const runtimeConfig = useRuntimeConfig();
  const route = useRoute();

  nuxtApp.hook('app:created', () => {
    hydrationStart = performance.now();
  });

  nuxtApp.hook('app:mounted', () => {
    if (hydrationStart !== undefined) {
      hydrationMs = performance.now() - hydrationStart;
    }
    // Hydration just finished — schedule a send
    scheduleSend();
  });

  const handleMetric = (metric: Metric) => {
    switch (metric.name) {
      case 'TTFB':
        vitals.ttfb = metric.value;
        break;
      case 'FCP':
        vitals.fcp = metric.value;
        break;
      case 'LCP':
        vitals.lcp = metric.value;
        break;
      case 'INP':
        vitals.inp = metric.value;
        break;
      case 'CLS':
        vitals.cls = metric.value;
        break;
    }
    // New metric arrived — schedule a send
    scheduleSend();
  };

  nuxtApp.hook('app:mounted', () => {
    import('web-vitals').then(({ onTTFB, onFCP, onLCP, onINP, onCLS }) => {
      onTTFB(handleMetric);
      onFCP(handleMetric);
      onLCP(handleMetric);
      onINP(handleMetric);
      onCLS(handleMetric);
    }).catch(() => {});
  });

  /**
   * Debounce: wait 1s after the last metric update, then send.
   * This batches metrics that arrive close together (TTFB+FCP ~100ms apart)
   * while sending as soon as a burst of metrics settles.
   *
   * Max 3 sends per page load to avoid spamming the server.
   */
  function scheduleSend(): void {
    if (sendCount >= 3) return;
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => doSend(), 1000);
  }

  function doSend(): void {
    if (sendCount >= 3) return;
    sendCount++;

    try {
      const baseURL = runtimeConfig.app?.baseURL || '/';
      const app = baseURL.replace(/^\/|\/$/g, '') || 'baseline';

      const nuxtDevice = nuxtApp.$device as Record<string, boolean> | undefined;
      let deviceType = 'unknown';
      if (nuxtDevice?.isMobile) deviceType = 'mobile';
      else if (nuxtDevice?.isTablet) deviceType = 'tablet';
      else if (nuxtDevice?.isDesktop) deviceType = 'desktop';

      const nav = navigator as any;
      const connectionType = nav.connection?.effectiveType || 'unknown';
      const payload = {
        app,
        route: route.path,
        deviceType,
        connectionType,
        vitals,
        hydration: hydrationMs,
        navigation: collectNavigationTiming(),
        resources: collectResourceSizes(),
        seq: sendCount, // 1 = first send, 2+ = updates
      };

      const url = `${baseURL}api/web-vitals`;
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });

      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, blob);
      } else {
        fetch(url, { method: 'POST', body: blob, keepalive: true }).catch(() => {});
      }
    } catch (error: unknown) {
      app.$logger.note({
        category: 'webVitals',
        message: 'sending metrics failed',
        level: 'warning',
        cause: error,
      });
    }
  }

  function collectNavigationTiming(): NavigationData {
    const data: NavigationData = {};
    try {
      const entries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
      if (entries.length > 0) {
        const nav = entries[0];
        if (nav.loadEventEnd > 0) data.loadDuration = nav.loadEventEnd - nav.startTime;
        if (nav.domContentLoadedEventEnd > 0) data.domContentLoaded = nav.domContentLoadedEventEnd - nav.startTime;
      }
    } catch (error: unknown) {
      app.$logger.note({
        category: 'webVitals',
        message: 'collecting navigation timing failed',
        level: 'warning',
        cause: error,
      });
    }
    return data;
  }

  function collectResourceSizes(): ResourceData {
    const data: ResourceData = { jsTransferBytes: 0, cssTransferBytes: 0, jsDecodedBytes: 0, jsCount: 0, cssCount: 0 };
    try {
      const entries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
      for (const entry of entries) {
        if (!entry.name.includes('/_nuxt/')) continue;
        if (entry.name.endsWith('.js') || entry.name.endsWith('.mjs')) {
          data.jsTransferBytes += entry.transferSize || 0;
          data.jsDecodedBytes += entry.decodedBodySize || 0;
          data.jsCount++;
        } else if (entry.name.endsWith('.css')) {
          data.cssTransferBytes += entry.transferSize || 0;
          data.cssCount++;
        }
      }
    } catch (error: unknown) {
      app.$logger.note({
        category: 'webVitals',
        message: 'collecting resource sizes failed',
        level: 'warning',
        cause: error,
      });
    }
    return data;
  }

  // Final send on visibilitychange — captures final LCP/CLS/INP values
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      if (debounceTimer) clearTimeout(debounceTimer);
      doSend();
    }
  });
});
